import { useTranslation } from 'react-i18next';
import { ScanLine, User } from 'lucide-react';
import { ImageViewerModal } from '@/components/ui/ImageViewerModal';

interface XrayUploadedModalProps {
  isOpen: boolean;
  patientName: string;
  imageCount: number;
  signedUrls: string[];
  photoIds?: string[];
  onClose: () => void;
}

export const XrayUploadedModal = ({
  isOpen,
  patientName,
  imageCount,
  signedUrls,
  photoIds,
  onClose,
}: XrayUploadedModalProps) => {
  const { t } = useTranslation();
  if (!isOpen || signedUrls.length === 0) return null;

  return (
    <>
      {/* Patient badge shown above the viewer */}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[201] flex items-center gap-2.5 px-4 py-2.5 bg-white rounded-2xl shadow-xl border border-neutral-200 animate-in slide-in-from-top-2 duration-200">
        <div className="w-8 h-8 rounded-md bg-primary-50 flex items-center justify-center flex-shrink-0">
          <ScanLine className="w-4 h-4 text-primary-600" />
        </div>
        <div>
          <p className="text-micro text-neutral-400 font-medium uppercase tracking-wide leading-none mb-0.5">
            {t('notifications.xrayUploadedModal.newXRay')}
          </p>
          <div className="flex items-center gap-1.5">
            <User className="w-3.5 h-3.5 text-secondary" />
            <p className="text-sm font-semibold text-secondary leading-none">
              {patientName}
              {imageCount > 1 && (
                <span className="ms-1.5 text-xs font-normal text-neutral-400">
                  {t('notifications.xrayUploadedModal.imageCount', {
                    count: imageCount,
                  })}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      <ImageViewerModal
        isOpen={isOpen}
        initialIndex={0}
        images={signedUrls.map((url, i) => ({
          url,
          label:
            imageCount > 1
              ? `X-Ray ${i + 1} of ${imageCount}`
              : `X-Ray — ${patientName}`,
          photoId: photoIds?.[i],
        }))}
        onClose={onClose}
      />
    </>
  );
};
